import styled from "styled-components";
import React, { Fragment } from "react";

import Box from "@mui/material/Box";
import Menu from "@mui/material/Menu";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import { logOut } from "@store/slices/user";
import { useIsAuth } from "@hooks/useIsAuth";
import MenuItem from "@mui/material/MenuItem";
import { NavSection } from "@components/atoms";
import IconButton from "@mui/material/IconButton";
import { getUserFullName } from "@selectors/auth";
import MoreIcon from "@mui/icons-material/MoreVert";
import { AccountCircle } from "@mui/icons-material";
import { useAppDispatch, useAppSelector } from "@hooks/store";

const Header = () => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [mobileMoreAnchorEl, setMobileMoreAnchorEl] =
    React.useState<null | HTMLElement>(null);

  const isAuth = useIsAuth();
  const dispatch = useAppDispatch();

  const userName = useAppSelector(getUserFullName);

  const isMenuOpen = Boolean(anchorEl);
  const isMobileMenuOpen = Boolean(mobileMoreAnchorEl);

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    const anchorEl = mobileMoreAnchorEl;
    handleMobileMenuClose();
    setAnchorEl(anchorEl || event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMoreAnchorEl(null);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    handleMobileMenuClose();
  };

  const onLogOutClick = () => {
    dispatch(logOut());
    handleMenuClose();
  };

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMoreAnchorEl(event.currentTarget);
  };

  const menuId = "primary-search-account-menu";

  const renderMenu = (
    <Menu
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      id={menuId}
      keepMounted
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      open={isMenuOpen}
      onClose={handleMenuClose}
    >
      <MenuItem onClick={onLogOutClick}>Log out</MenuItem>
    </Menu>
  );

  const mobileMenuId = "primary-search-account-menu-mobile";

  const renderMobileMenu = (
    <Menu
      anchorEl={mobileMoreAnchorEl}
      anchorOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      id={mobileMenuId}
      keepMounted
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      open={isMobileMenuOpen}
      onClose={handleMobileMenuClose}
    >
      <MenuItem onClick={handleProfileMenuOpen}>
        <IconButton
          size="large"
          aria-label="account of current user"
          aria-controls="primary-search-account-menu"
          aria-haspopup="true"
          color="inherit"
        >
          <AccountCircle />
        </IconButton>
        <p>Profile</p>
      </MenuItem>
    </Menu>
  );

  return (
    <Wrapper>
      <Box sx={{ flexGrow: 1 }}>
        <AppBar position="static">
          <Toolbar style={{ backgroundColor: "white", minHeight: "64px" }}>
            <NavSection />

            <Box sx={{ flexGrow: 1 }} />

            {isAuth && (
              <Fragment>
                <Box
                  sx={{
                    display: { xs: "none", md: "flex", alignItems: "center" },
                  }}
                >
                  <AccountTitle>{userName}</AccountTitle>
                  <IconButton
                    size="large"
                    edge="end"
                    aria-label="account of current user"
                    aria-controls={menuId}
                    aria-haspopup="true"
                    onClick={handleProfileMenuOpen}
                    sx={{ display: { color: "#1976d2" } }}
                  >
                    <AccountCircle />
                  </IconButton>
                </Box>

                <Box sx={{ display: { xs: "flex", md: "none" } }}>
                  <IconButton
                    size="large"
                    aria-label="show more"
                    aria-controls={mobileMenuId}
                    aria-haspopup="true"
                    onClick={handleMobileMenuOpen}
                    sx={{ display: { color: "#1976d2" } }}
                  >
                    <MoreIcon />
                  </IconButton>
                </Box>
              </Fragment>
            )}
          </Toolbar>
        </AppBar>

        {isAuth && renderMobileMenu}
        {isAuth && renderMenu}
      </Box>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  width: 100%;
  height: 64px;
`;

const AccountTitle = styled.span`
  font-size: 12pt;
  color: #1976d2;
`;

export default Header;
